/**
 * Ring casting technical cross-section diagram
 * Shows ring band cross-section, stone setting, prongs, and dimension annotation.
 * Technical illustration style — for the manufacturing partner page.
 */
export function CastingDiagram({
  className = "",
  animated = true,
}: {
  className?: string;
  animated?: boolean;
}) {
  const stroke = "oklch(0.085 0.004 50)";
  const gold = "oklch(0.48 0.072 52)";
  const muted = "oklch(0.47 0.008 55)";

  return (
    <svg
      viewBox="0 0 480 400"
      className={className}
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      <defs>
        <style>{`
          @keyframes diagram-fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          .diagram-part {
            opacity: 0;
            animation: diagram-fade-in 0.6s ease forwards;
          }
          @media (prefers-reduced-motion: reduce) {
            .diagram-part { opacity: 1 !important; animation: none !important; }
          }
        `}</style>
        <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6 Z" fill={stroke} opacity="0.35" />
        </marker>
        <marker id="arrowhead-rev" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto-start-reverse">
          <path d="M 0 0 L 6 3 L 0 6 Z" fill={stroke} opacity="0.35" />
        </marker>
      </defs>

      {/* ── Background grid ── */}
      <g opacity="0.04">
        {Array.from({ length: 16 }).map((_, i) => (
          <line key={`vg${i}`} x1={i * 32} y1="0" x2={i * 32} y2="400" stroke={stroke} strokeWidth="0.5" />
        ))}
        {Array.from({ length: 13 }).map((_, i) => (
          <line key={`hg${i}`} x1="0" y1={i * 32} x2="480" y2={i * 32} stroke={stroke} strokeWidth="0.5" />
        ))}
      </g>

      {/* ── Ring band cross-section — side view ── */}
      {/* Band is shown as two trapezoidal halves (shank taper) */}
      <g className="diagram-part" style={animated ? { animationDelay: "0.1s" } : { opacity: 1 }}>
        {/* Left half of shank */}
        <path
          d="M 100 320 L 130 200 L 150 200 L 120 320 Z"
          fill="none" stroke={stroke} strokeWidth="1.2" strokeOpacity="0.7"
        />
        {/* Right half of shank */}
        <path
          d="M 380 320 L 350 200 L 330 200 L 360 320 Z"
          fill="none" stroke={stroke} strokeWidth="1.2" strokeOpacity="0.7"
        />
        {/* Base (finger contact arc — simplified as straight) */}
        <path
          d="M 100 320 C 180 345, 300 345, 380 320"
          fill="none" stroke={stroke} strokeWidth="1.2" strokeOpacity="0.7"
        />
        {/* Inner face of shank */}
        <path
          d="M 120 320 C 190 338, 290 338, 360 320"
          fill="none" stroke={stroke} strokeWidth="0.7" strokeOpacity="0.4"
        />

        {/* Hatch fill lines on band cross-section (material fill indication) */}
        {[8, 16, 24, 32, 40, 48, 56, 64].map((offset) => (
          <g key={offset}>
            <line
              x1={100 + offset * 0.375} y1={320 - offset * 0.8}
              x2={110 + offset * 0.375} y2={320 - offset * 0.8 - 12}
              stroke={stroke} strokeWidth="0.4" strokeOpacity="0.15"
            />
            <line
              x1={380 - offset * 0.375} y1={320 - offset * 0.8}
              x2={370 - offset * 0.375} y2={320 - offset * 0.8 - 12}
              stroke={stroke} strokeWidth="0.4" strokeOpacity="0.15"
            />
          </g>
        ))}
      </g>

      {/* ── Setting tube / bezel base ── */}
      <g className="diagram-part" style={animated ? { animationDelay: "0.3s" } : { opacity: 1 }}>
        {/* Outer walls of setting */}
        <line x1="150" y1="200" x2="148" y2="155" stroke={stroke} strokeWidth="1.1" strokeOpacity="0.7" />
        <line x1="330" y1="200" x2="332" y2="155" stroke={stroke} strokeWidth="1.1" strokeOpacity="0.7" />
        {/* Setting base plate */}
        <line x1="148" y1="170" x2="332" y2="170" stroke={stroke} strokeWidth="0.8" strokeOpacity="0.5" />
        {/* Setting gallery rail */}
        <line x1="148" y1="180" x2="332" y2="180" stroke={stroke} strokeWidth="0.6" strokeOpacity="0.35" strokeDasharray="4 3" />
        {/* Setting side hatch */}
        {[0, 8, 16, 24, 32, 40, 48].map((o) => (
          <line key={o}
            x1={148} y1={155 + o * 0.58}
            x2={155} y2={148 + o * 0.58}
            stroke={stroke} strokeWidth="0.4" strokeOpacity="0.12"
          />
        ))}
        {[0, 8, 16, 24, 32, 40, 48].map((o) => (
          <line key={o}
            x1={332} y1={155 + o * 0.58}
            x2={325} y2={148 + o * 0.58}
            stroke={stroke} strokeWidth="0.4" strokeOpacity="0.12"
          />
        ))}
      </g>

      {/* ── Four prongs ── */}
      <g className="diagram-part" style={animated ? { animationDelay: "0.5s" } : { opacity: 1 }}>
        {/* Left prong pair */}
        <path d="M 163 155 C 160 140, 159 125, 162 115 C 164 108, 168 106, 172 108" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.8" />
        <path d="M 178 155 C 177 142, 175 128, 175 118 C 175 110, 177 106, 180 105" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.8" />
        {/* Right prong pair */}
        <path d="M 317 155 C 320 140, 321 125, 318 115 C 316 108, 312 106, 308 108" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.8" />
        <path d="M 302 155 C 303 142, 305 128, 305 118 C 305 110, 303 106, 300 105" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.8" />
      </g>

      {/* ── Gemstone — princess cut (square, side view = trapezoidal) ── */}
      <g className="diagram-part" style={animated ? { animationDelay: "0.7s" } : { opacity: 1 }}>
        {/* Table (top face) */}
        <line x1="182" y1="108" x2="298" y2="108" stroke={gold} strokeWidth="1.5" strokeOpacity="0.9" />
        {/* Left crown facet */}
        <line x1="172" y1="115" x2="182" y2="108" stroke={gold} strokeWidth="1" strokeOpacity="0.7" />
        {/* Right crown facet */}
        <line x1="308" y1="115" x2="298" y2="108" stroke={gold} strokeWidth="1" strokeOpacity="0.7" />
        {/* Girdle lines */}
        <line x1="172" y1="115" x2="308" y2="115" stroke={gold} strokeWidth="0.8" strokeOpacity="0.6" />
        {/* Pavilion to culet */}
        <path d="M 172 115 L 234 148 M 308 115 L 246 148 M 234 148 L 240 155 L 246 148" fill="none" stroke={gold} strokeWidth="0.9" strokeOpacity="0.55" />
        {/* Crown facets detail */}
        <line x1="210" y1="108" x2="200" y2="115" stroke={gold} strokeWidth="0.6" strokeOpacity="0.4" />
        <line x1="230" y1="108" x2="225" y2="115" stroke={gold} strokeWidth="0.6" strokeOpacity="0.4" />
        <line x1="250" y1="108" x2="255" y2="115" stroke={gold} strokeWidth="0.6" strokeOpacity="0.4" />
        <line x1="270" y1="108" x2="280" y2="115" stroke={gold} strokeWidth="0.6" strokeOpacity="0.4" />

        {/* Sparkle */}
        {[[240, 88], [215, 95], [265, 95]].map(([sx, sy], i) => (
          <g key={i} opacity="0.6">
            <line x1={sx - 5} y1={sy} x2={sx + 5} y2={sy} stroke={gold} strokeWidth="0.8" />
            <line x1={sx} y1={sy - 5} x2={sx} y2={sy + 5} stroke={gold} strokeWidth="0.8" />
            <line x1={sx - 3} y1={sy - 3} x2={sx + 3} y2={sy + 3} stroke={gold} strokeWidth="0.5" />
            <line x1={sx + 3} y1={sy - 3} x2={sx - 3} y2={sy + 3} stroke={gold} strokeWidth="0.5" />
          </g>
        ))}
      </g>

      {/* ── Dimension / annotation lines ── */}
      <g className="diagram-part" opacity="0.5" style={animated ? { animationDelay: "1s" } : { opacity: 0.5 }}>
        {/* Stone width */}
        <line x1="172" y1="72" x2="308" y2="72" stroke={stroke} strokeWidth="0.6"
          markerStart="url(#arrowhead-rev)" markerEnd="url(#arrowhead)" strokeOpacity="0.4" />
        <line x1="172" y1="68" x2="172" y2="82" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="308" y1="68" x2="308" y2="82" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.3" />

        {/* Band width */}
        <line x1="60" y1="320" x2="420" y2="320" stroke={stroke} strokeWidth="0.6"
          markerStart="url(#arrowhead-rev)" markerEnd="url(#arrowhead)" strokeOpacity="0.35" />
        <line x1="100" y1="315" x2="100" y2="330" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.25" />
        <line x1="380" y1="315" x2="380" y2="330" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.25" />

        {/* Setting height */}
        <line x1="355" y1="108" x2="355" y2="200" stroke={stroke} strokeWidth="0.6"
          markerStart="url(#arrowhead-rev)" markerEnd="url(#arrowhead)" strokeOpacity="0.35" />
        <line x1="350" y1="108" x2="360" y2="108" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="350" y1="200" x2="360" y2="200" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.3" />
      </g>

      {/* ── Phase labels ── */}
      <g className="diagram-part" style={animated ? { animationDelay: "1.2s" } : { opacity: 1 }}>
        {/* CAST */}
        <text x="115" y="365" fontFamily="var(--font-sans)" fontSize="7.5" fill={stroke} opacity="0.4" letterSpacing="2.5">CAST</text>
        <line x1="130" y1="350" x2="130" y2="330" stroke={stroke} strokeWidth="0.5" strokeDasharray="3 3" strokeOpacity="0.2" />

        {/* SET */}
        <text x="224" y="192" fontFamily="var(--font-sans)" fontSize="7.5" fill={stroke} opacity="0.4" letterSpacing="2.5">SET</text>
        <line x1="235" y1="196" x2="235" y2="170" stroke={stroke} strokeWidth="0.5" strokeDasharray="3 3" strokeOpacity="0.2" />

        {/* PLATE */}
        <text x="29" y="252" fontFamily="var(--font-sans)" fontSize="7.5" fill={gold} opacity="0.45" letterSpacing="2.5">PLATE</text>
        <line x1="72" y1="250" x2="130" y2="250" stroke={gold} strokeWidth="0.5" strokeDasharray="3 3" strokeOpacity="0.2" />

        {/* Stone label */}
        <text x="180" y="62" fontFamily="var(--font-sans)" fontSize="7" fill={muted} opacity="0.45" letterSpacing="2">PRINCESS CUT</text>

        {/* Band label */}
        <text x="218" y="378" fontFamily="var(--font-sans)" fontSize="7" fill={muted} opacity="0.35" letterSpacing="2">SHANK</text>
      </g>
    </svg>
  );
}
